/**
 * Accordion Component
 *
 * A flexible, accessible accordion component with support for:
 * - Single or multiple item expansion
 * - Various visual styles and variants
 * - Keyboard navigation and accessibility
 * - Controlled and uncontrolled modes
 * - Animation and transitions
 */

import React, {
  useState,
  useEffect,
  useRef,
  createContext,
  useContext,
} from "react";
import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { ComponentErrorBoundary } from "../ErrorBoundary";

/**
 * Accordion Context for managing state across items
 */
const AccordionContext = createContext(null);

/**
 * Main Accordion Container Component
 */
export default function Accordion({
  children,
  type = "single", // 'single' | 'multiple'
  variant = "default", // 'default' | 'bordered' | 'filled' | 'minimal'
  size = "md", // 'sm' | 'md' | 'lg'
  defaultValue = null,
  value,
  onValueChange,
  collapsible = false, // Only for single type - allows closing all items
  disabled = false,
  className = "",
  itemClassName = "",
  contentClassName = "",
  ...props
}) {
  const [internalValue, setInternalValue] = useState(() => {
    if (type === "multiple") {
      return defaultValue || [];
    }
    return defaultValue;
  });

  // Use controlled value if provided, otherwise use internal state
  const currentValue = value !== undefined ? value : internalValue;

  const handleValueChange = (newValue) => {
    if (disabled) return;

    if (value === undefined) {
      setInternalValue(newValue);
    }
    onValueChange?.(newValue);
  };

  const toggleItem = (itemValue) => {
    if (type === "multiple") {
      const newValue = Array.isArray(currentValue) ? [...currentValue] : [];
      const index = newValue.indexOf(itemValue);

      if (index > -1) {
        newValue.splice(index, 1);
      } else {
        newValue.push(itemValue);
      }

      handleValueChange(newValue);
    } else {
      // Single mode
      const newValue =
        currentValue === itemValue && collapsible ? null : itemValue;
      handleValueChange(newValue);
    }
  };

  const isItemOpen = (itemValue) => {
    if (type === "multiple") {
      return Array.isArray(currentValue) && currentValue.includes(itemValue);
    }
    return currentValue === itemValue;
  };

  // Base accordion styles
  const baseClasses = `accordion-root ${className}`;

  const variantClasses = {
    default: "space-y-2",
    bordered: "border border-gray-200 rounded-lg divide-y divide-gray-200",
    filled: "bg-gray-50 rounded-lg divide-y divide-gray-200",
    minimal: "space-y-0",
  };

  const contextValue = {
    type,
    variant,
    size,
    disabled,
    itemClassName,
    contentClassName,
    toggleItem,
    isItemOpen,
  };

  return (
    <ComponentErrorBoundary componentName="Accordion">
      <AccordionContext.Provider value={contextValue}>
        <div className={`${baseClasses} ${variantClasses[variant]}`} {...props}>
          {children}
        </div>
      </AccordionContext.Provider>
    </ComponentErrorBoundary>
  );
}

/**
 * Individual Accordion Item Component
 */
// Create item context for passing value down to trigger and content
const AccordionItemContext = createContext(null);

export function AccordionItem({
  children,
  value,
  disabled = false,
  className = "",
  ...props
}) {
  const context = useContext(AccordionContext);

  if (!context) {
    throw new Error("AccordionItem must be used within an Accordion");
  }

  const { variant, itemClassName } = context;
  const isDisabled = disabled || context.disabled;

  // Item container styles
  const variantClasses = {
    default: "bg-white rounded-lg border border-gray-200",
    bordered: "",
    filled: "bg-white first:rounded-t-lg last:rounded-b-lg",
    minimal: "border-b border-gray-200 last:border-b-0",
  };

  const disabledClasses = isDisabled ? "opacity-50 cursor-not-allowed" : "";

  return (
    <AccordionItemContext.Provider value={{ value, disabled: isDisabled }}>
      <div
        className={`accordion-item ${variantClasses[variant]} ${disabledClasses} ${itemClassName} ${className}`}
        data-value={value}
        data-disabled={isDisabled}
        {...props}
      >
        {children}
      </div>
    </AccordionItemContext.Provider>
  );
}

/**
 * Accordion Trigger (Header/Button) Component
 */
export function AccordionTrigger({
  children,
  className = "",
  iconPosition = "right", // 'left' | 'right' | 'none'
  icon: CustomIcon,
  ...props
}) {
  const context = useContext(AccordionContext);
  const itemContext = useContext(AccordionItemContext);

  if (!context) {
    throw new Error("AccordionTrigger must be used within an AccordionItem");
  }

  if (!itemContext) {
    throw new Error("AccordionTrigger must be used within an AccordionItem");
  }

  const { size, variant, disabled, toggleItem, isItemOpen } = context;
  const { value: itemValue, disabled: itemDisabled } = itemContext;

  const itemRef = useRef(null);
  const isOpen = itemValue ? isItemOpen(itemValue) : false;
  const isDisabled = disabled || itemDisabled;

  const handleClick = () => {
    if (!isDisabled && itemValue) {
      toggleItem(itemValue);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleClick();
    }
  };

  // Size classes
  const sizeClasses = {
    sm: "px-3 py-2 text-sm",
    md: "px-4 py-3 text-base",
    lg: "px-6 py-4 text-lg",
  };

  // Variant classes
  const variantClasses = {
    default: "hover:bg-gray-50",
    bordered: "hover:bg-gray-50",
    filled: "hover:bg-gray-100",
    minimal: "hover:bg-gray-50",
  };

  const baseClasses = `
    accordion-trigger w-full flex items-center justify-between font-medium text-left
    transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset
  `;

  const disabledClasses = isDisabled ? "cursor-not-allowed" : "cursor-pointer";

  // Choose icon component
  const IconComponent =
    CustomIcon || (isOpen ? ChevronDownIcon : ChevronRightIcon);

  return (
    <button
      ref={itemRef}
      className={`${baseClasses} ${sizeClasses[size]} ${variantClasses[variant]} ${disabledClasses} ${className}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      aria-expanded={isOpen}
      disabled={isDisabled}
      {...props}
    >
      {iconPosition === "left" && (
        <div className="flex-shrink-0 mr-3">
          <IconComponent className="h-5 w-5 text-gray-500 transition-transform" />
        </div>
      )}

      <div className="flex-1 text-left">{children}</div>

      {iconPosition === "right" && (
        <div className="flex-shrink-0 ml-3">
          <IconComponent className="h-5 w-5 text-gray-500 transition-transform" />
        </div>
      )}
    </button>
  );
}

/**
 * Accordion Content Component
 */
export function AccordionContent({
  children,
  className = "",
  forceMount = false,
  ...props
}) {
  const context = useContext(AccordionContext);
  const itemContext = useContext(AccordionItemContext);

  if (!context) {
    throw new Error("AccordionContent must be used within an AccordionItem");
  }

  if (!itemContext) {
    throw new Error("AccordionContent must be used within an AccordionItem");
  }

  const { size, contentClassName } = context;
  const { value: itemValue } = itemContext;

  const contentRef = useRef(null);
  const isOpen = itemValue ? context.isItemOpen(itemValue) : false;

  // Size classes for content padding
  const sizeClasses = {
    sm: "px-3 pb-2",
    md: "px-4 pb-3",
    lg: "px-6 pb-4",
  };

  const baseClasses = `accordion-content overflow-hidden transition-all duration-200 ease-in-out`;

  // Don't render if not open and not force mounted
  if (!isOpen && !forceMount) {
    return null;
  }

  return (
    <div
      ref={contentRef}
      className={`${baseClasses} ${isOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"} ${sizeClasses[size]} ${contentClassName} ${className}`}
      {...props}
    >
      <div className="pt-1">{children}</div>
    </div>
  );
}

/**
 * Navigation-specific Accordion for Sidebar use
 */
export function NavigationAccordion({
  items = [],
  className = "",
  onItemClick,
  currentPath,
  renderItem,
  renderContent,
  ...props
}) {
  const [openItems, setOpenItems] = useState(new Set());

  // Auto-open items that have active children
  useEffect(() => {
    if (currentPath) {
      const newOpenItems = new Set();

      items.forEach((item, index) => {
        if (
          item.children &&
          item.children.some((child) => child.href === currentPath)
        ) {
          newOpenItems.add(String(index));
        }
      });

      setOpenItems(newOpenItems);
    }
  }, [currentPath, items]);

  const handleToggle = (itemIndex) => {
    const newOpenItems = new Set(openItems);
    if (newOpenItems.has(String(itemIndex))) {
      newOpenItems.delete(String(itemIndex));
    } else {
      newOpenItems.add(String(itemIndex));
    }
    setOpenItems(newOpenItems);
  };

  return (
    <Accordion
      type="multiple"
      variant="minimal"
      value={Array.from(openItems)}
      onValueChange={(values) => setOpenItems(new Set(values))}
      className={className}
      {...props}
    >
      {items.map((item, index) =>
        /* Handle items with children (accordion) vs items with href (direct navigation) */
        item.children && item.children.length > 0 ? (
          <AccordionItem
            key={item.key || item.href || index}
            value={String(index)}
          >
            <AccordionTrigger>
              {renderItem ? (
                renderItem(item, index)
              ) : (
                <div className="flex items-center space-x-3">
                  {item.icon && (
                    <span className="text-lg flex-shrink-0">{item.icon}</span>
                  )}
                  <span>{item.label || item.name}</span>
                  {item.badge && (
                    <span
                      className={`ml-auto px-2 py-0.5 text-xs rounded-full ${
                        item.badge.color === "blue"
                          ? "bg-blue-100 text-blue-800"
                          : item.badge.color === "green"
                            ? "bg-green-100 text-green-800"
                            : item.badge.color === "purple"
                              ? "bg-purple-100 text-purple-800"
                              : item.badge.color === "red"
                                ? "bg-red-100 text-red-800"
                                : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {typeof item.badge === "object"
                        ? item.badge.count
                        : item.badge}
                    </span>
                  )}
                </div>
              )}
            </AccordionTrigger>

            <AccordionContent>
              {renderContent ? (
                renderContent(item, index)
              ) : (
                <div className="space-y-1">
                  {item.children?.map((child, childIndex) => (
                    <a
                      key={child.key || child.href || childIndex}
                      href={child.href}
                      className={`
                        block px-3 py-2 text-sm rounded-md transition-colors
                        ${
                          currentPath === child.href
                            ? "bg-blue-50 text-blue-700 font-medium"
                            : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                        }
                      `}
                      onClick={(e) => onItemClick?.(e, child, childIndex)}
                    >
                      {child.label || child.name}
                    </a>
                  ))}
                </div>
              )}
            </AccordionContent>
          </AccordionItem>
        ) : (
          /* Direct navigation item with href */
          <div
            key={item.key || item.href || index}
            className={`
              px-3 py-2 font-medium rounded-lg transition-all duration-200 cursor-pointer
              ${
                currentPath === item.href
                  ? "bg-blue-50 text-blue-700 border-l-4 border-blue-600"
                  : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
              }
            `}
            onClick={(e) => onItemClick?.(e, item, index)}
          >
            <div className="flex items-center space-x-3">
              {item.icon && (
                <span className="text-lg flex-shrink-0">{item.icon}</span>
              )}
              <span>{item.label || item.name}</span>
              {item.badge && (
                <span
                  className={`ml-auto px-2 py-0.5 text-xs rounded-full ${
                    item.badge.color === "blue"
                      ? "bg-blue-100 text-blue-800"
                      : item.badge.color === "green"
                        ? "bg-green-100 text-green-800"
                        : item.badge.color === "purple"
                          ? "bg-purple-100 text-purple-800"
                          : item.badge.color === "red"
                            ? "bg-red-100 text-red-800"
                            : "bg-gray-100 text-gray-800"
                  }`}
                >
                  {typeof item.badge === "object"
                    ? item.badge.count
                    : item.badge}
                </span>
              )}
            </div>
          </div>
        )
      )}
    </Accordion>
  );
}

/**
 * FAQ-style Accordion
 */
export function FAQAccordion({
  faqs = [],
  className = "",
  searchable = false,
  ...props
}) {
  const [searchTerm, setSearchTerm] = useState("");

  const filteredFaqs =
    searchable && searchTerm
      ? faqs.filter(
          (faq) =>
            faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
            faq.answer.toLowerCase().includes(searchTerm.toLowerCase())
        )
      : faqs;

  return (
    <div className={className}>
      {searchable && (
        <div className="mb-4">
          <input
            type="text"
            placeholder="Search FAQ..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      )}

      <Accordion type="single" variant="bordered" collapsible {...props}>
        {filteredFaqs.map((faq, index) => (
          <AccordionItem key={faq.id || index} value={String(index)}>
            <AccordionTrigger className="text-left">
              {faq.question}
            </AccordionTrigger>
            <AccordionContent>
              <div className="text-gray-700 leading-relaxed">{faq.answer}</div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
}

/**
 * Settings/Preferences Accordion
 */
export function SettingsAccordion({ settings = [], className = "", ...props }) {
  return (
    <Accordion
      type="multiple"
      variant="filled"
      className={className}
      {...props}
    >
      {settings.map((section, index) => (
        <AccordionItem key={section.id || index} value={String(index)}>
          <AccordionTrigger>
            <div className="flex items-center space-x-3">
              {section.icon && <span className="text-lg">{section.icon}</span>}
              <div>
                <div className="font-medium">{section.title}</div>
                {section.description && (
                  <div className="text-sm text-gray-500">
                    {section.description}
                  </div>
                )}
              </div>
            </div>
          </AccordionTrigger>

          <AccordionContent>
            <div className="space-y-4">
              {section.settings?.map((setting, settingIndex) => (
                <div
                  key={setting.id || settingIndex}
                  className="flex items-center justify-between"
                >
                  <div>
                    <div className="font-medium text-sm">{setting.label}</div>
                    {setting.description && (
                      <div className="text-xs text-gray-500">
                        {setting.description}
                      </div>
                    )}
                  </div>
                  {setting.component}
                </div>
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}
